import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { maxLength, helpers } from '@vuelidate/validators'
import validationMixinGeneral from '@apps/order-form/src/mixins/validationMixinGeneral'
import { LAST_ORDER_MODULE } from '@apps/order-form/src/store/modules/lastOrderModule'
import { ADDRESS_BOOK_STORE } from '@constants/store'
import { GENERAL_STORE } from '../store'

export default {
  mixins: [validationMixinGeneral],

  props: {
    dot: {
      type: Object,
      required: true,
    },
  },

  validations() {
    return {
      apartment: {
        maxLength: helpers.withMessage(
          ({ $model, $params }) =>
            `Максимальная длина поля "кв./офис" превышена на ${
              $model.length - $params.max
            } символов`,
          maxLength(10)
        ),
      },
    }
  },

  computed: {
    ...mapState(GENERAL_STORE, ['archiveAddressSuggestions', 'base', 'form', 'order']),
    ...mapGetters(GENERAL_STORE, [
      'selectedCity',
      'getDotIndex',
      'isBuyout',
      'isAnonimousForm',
      'isAdmin',
    ]),

    addressInputRules() {
      if (this.isAdmin) {
        return {
          addressStreet: null,
          addressBuilding: null,
        }
      }

      return {}
    },
    dotAddress() {
      return this.dot.address
    },
    dotBuilding() {
      return this.dot.building
    },

    lastOrderSuggestions() {
      return this.$store.hasModule(LAST_ORDER_MODULE)
        ? this.$store.getters[`${LAST_ORDER_MODULE}/lastOrderSuggestions`]
        : false
    },
    addressInputValidator() {
      return this.$refs.addressInput?.v$
    },

    locations() {
      return this.form.allCities.find(city => +city.id === +this.order.city).regions
    },

    addressFieldRef() {
      return this.$refs.addressInput.addressFieldRef
    },

    apartment: {
      get() {
        return this.dot.apartment
      },
      set(value) {
        this.updateDot({
          dot: this.dot,
          data: {
            key: 'apartment',
            value,
          },
        })
      },
    },
    entrance: {
      get() {
        return this.dot.entrance
      },
      set(value) {
        this.updateDot({
          dot: this.dot,
          data: {
            key: 'entrance',
            value,
          },
        })
      },
    },
    storey: {
      get() {
        return this.dot.storey
      },
      set(value) {
        this.updateDot({
          dot: this.dot,
          data: {
            key: 'storey',
            value,
          },
        })
      },
    },

    thisDotIndex() {
      return this.getDotIndex(this.dot)
    },

    computedPlaceholder() {
      const placeholders = this.selectedCity.placeholder
      const index = this.thisDotIndex

      // Для первой точки, или если есть только один плейсхолдер
      if (placeholders.length === 1 || !index) {
        return placeholders[0]
      }

      // Пытаемся подставить для каждой точки свой плейсхолдер,
      // либо, если такого нет, второй по списку
      return placeholders[index] || placeholders[1]
    },

    draftSuggestions() {
      const suggestions = this.form?.orderDraft?.filter(draft => draft.is_suggestion) || []

      const addressBookItems =
        this.form?.orderDraft?.filter(draft => draft.is_addressBookItem) || []

      const drafts = !this.thisDotIndex ? [...addressBookItems, ...suggestions] : addressBookItems

      if (!this.lastOrderSuggestions) return drafts

      // Если индекс точки > 0 - точка доставки, получаем точки доставки из последнего заказа
      let lastOrderDotsSuggestions = this.thisDotIndex
        ? this.lastOrderSuggestions?.deliveryDots || []
        : this.lastOrderSuggestions?.pickupDot || []

      // Если точка забора, вернется именно точка, устанавливаем массив
      if (!Array.isArray(lastOrderDotsSuggestions))
        lastOrderDotsSuggestions = [lastOrderDotsSuggestions]

      // Для точек доставки недавние адреса ставим в конец
      const draftsWithLastDots = [...addressBookItems, ...lastOrderDotsSuggestions]

      return !this.thisDotIndex ? [...draftsWithLastDots, ...suggestions] : draftsWithLastDots
    },

    fullAddress() {
      return String(this.dotAddress) + String(this.dotBuilding)
    },
  },

  methods: {
    ...mapMutations(GENERAL_STORE, ['updateDot', 'cleanDotAddress', 'toggleModals']),
    ...mapActions(GENERAL_STORE, [
      'getAddressSuggestions',
      'setDraftSuggestion',
      'deleteSuggestion',
      'showMapModal',
      'calculatePrice',
      'getClosestMetro',
      'setAddressItemState',
      'deleteAddressBookItem',
    ]),

    handleSuggestionClick(suggestion, targetDot = null) {
      // Оставляем обратную совместимость, где передаётся только рекомендация и добавляем возможную целевую точку
      if (suggestion.is_suggestion) this.setDraftSuggestion({ suggestion, targetDot })
      else if (suggestion.is_addressBookItem)
        this.$store.dispatch(`${ADDRESS_BOOK_STORE}/setAddressItemDot`, { suggestion, targetDot })
      else if (suggestion.is_lastOrderItem)
        this.$store.dispatch(`${LAST_ORDER_MODULE}/setLastOrderDot`, { suggestion, targetDot })
    },
    handleDeleteSuggestion(suggestion) {
      if (suggestion.is_suggestion) this.deleteSuggestion(suggestion)
      else if (suggestion.is_addressBookItem) this.deleteAddressBookItem(suggestion)
    },

    setReturnDot() {
      const params = [
        'address',
        'street',
        'building',
        'apartment',
        'storey',
        'entrance',
        'lat',
        'lng',
        'phone',
        'contact',
        'comment',
      ]

      const data = []

      params.forEach(key => {
        data.push({
          key,
          value: this.order.dots[0][key],
        })
        data.push({
          key: 'isSuggestionSet',
          value: true,
        })
      })

      this.updateDot({
        dot: this.dot,
        data,
      })
    },

    chooseAddress() {
      this.showMapModal(this.dot)
    },

    async handleClosestMetro() {
      if (!this.base.isAdmin) return

      if (!this.fullAddress || this.fullAddress === 'null') {
        this.updateDot({ dot: this.dot, data: { key: 'metroId', value: '' } })
        return
      }

      const dotTarget = this.dot

      if (!dotTarget.suburb) dotTarget.subrub = this.selectedCity.name
      this.getClosestMetro({ dot: dotTarget, cityId: this.selectedCity.id })
    },
  },

  watch: {
    async fullAddress(newAddress, oldAddress) {
      // нужно, чтобы не менять id метро при редактировании заказа во время инициализации формы
      if ((!oldAddress || oldAddress === 'null') && this.base.isEdit) return

      this.handleClosestMetro()
    },
  },

  mounted() {
    this.addFieldValidator('apartment', this.addressFieldRef)

    const addressValidator = {
      touch: () => this.$refs.addressInput.touchV(),
      hasErrors: () => this.$refs.addressInput.hasError,
      elRef: this.addressFieldRef,
    }

    this.validators.push(addressValidator)

    this.addValidator(addressValidator)
  },
}
